import React, { useState, useEffect, useMemo, useRef } from "react";
import { pgProgrammesApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./PGProgrammes.scss";

const DEPT_COLORS = {
  "Faculty of Science":      "#0ea5e9",
  "Faculty of Arts":         "#8b5cf6",
  "Faculty of Commerce":     "#f59e0b",
  "Faculty of Education":    "#10b981",
  "Faculty of Law":          "#ef4444",
  "Faculty of Management":   "#f97316",
  "Faculty of Technology":   "#06b6d4",
  "Faculty of Social Work":  "#ec4899",
};
const DEFAULT_COLOR = "#64748b";
const getDeptColor = (dept) => DEPT_COLORS[dept] || DEFAULT_COLOR;

const MODES = ["Full Time", "Part Time", "Distance"];

const mkProgrammeSubject = () => ({
  id: Date.now() + Math.random(),
  Subject_Name: "",
  Total_Seats: "",
  Group: ""
});

const defaultForm = {
  _id: null,
  Programme_Name: "",
  Programme_Code: "",
  Department_Faculty: "",
  Programme_Description: "",
  Programme_Highlights: "",
  Specialization: "",
  Duration: "",
  Total_Semesters: "",
  Total_Seats: "",
  Annual_Fees: "",
  Mode_of_Study: "Full Time",
  Affiliation: "",
  Accreditation_Approval: "",
  Minimum_Qualification: "",
  Eligibility_Criteria: "",
  Programme_Subjects: [],
  Status: "Draft",
};

const PGProgrammes = () => {
  const [programmes, setProgrammes]         = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(defaultForm);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [confirmAction, setConfirmAction]   = useState("");
  const [searchText, setSearchText]         = useState("");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [filterDept, setFilterDept]         = useState("all");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  const [formError, setFormError]           = useState("");

  useEffect(() => { fetchProgrammes(); }, []);

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  const fetchProgrammes = async () => {
    try {
      setLoading(true);
      const res = await apiClient(pgProgrammesApi);
      const result = await res.json();
      if (res.ok) setProgrammes(result.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? pgProgrammesApi : `${pgProgrammesApi}/${formData._id}`;

      // Clean payload: strip auto-generated Mongo metadata fields
      const { __v, createdAt, updatedAt, isDeleted, ...cleanData } = formData;
      if (modalType === "add") delete cleanData._id;

      const res = await apiClient(url, { method, body: JSON.stringify(cleanData) });
      const result = await res.json().catch(() => ({}));

      if (res.ok && result.success !== false) {
        fetchProgrammes();
        closeModal();
      } else {
        const errMsg = result.message || result.error || `Failed to save programme (HTTP ${res.status})`;
        setFormError(errMsg);
      }
    } catch (err) {
      console.error("Error saving programme:", err);
      setFormError("Network error or server unreachable. Please try again.");
    }
    finally { setLoading(false); }
  };

  const handleTogglePublish = async (id) => {
    const prog = programmes.find(p => p._id === id);
    const newStatus = prog.Status === "Published" ? "Draft" : "Published";
    try {
      setLoading(true);
      await apiClient(`${pgProgrammesApi}/${id}`, { method: "PUT", body: JSON.stringify({ ...prog, Status: newStatus }) });
      fetchProgrammes(); setConfirmId(null); setConfirmAction("");
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${pgProgrammesApi}/${id}`, { method: "DELETE" });
      fetchProgrammes(); setConfirmId(null); setConfirmAction("");
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openAddModal  = () => { 
    setFormData(defaultForm); 
    setFormError("");
    setModalType("add"); 
    setDropdownOpenId(null); 
  };
  const openEditModal = (p) => { 
    if (!p) return;
    setFormData({ ...defaultForm, ...p }); 
    setFormError("");
    setModalType("edit"); 
    setDropdownOpenId(null); 
  };
  const openViewModal = (p) => {
    if (!p) return;
    setSelectedItem(p);
    setModalType("view");
    setDropdownOpenId(null);
  };
  const closeModal    = () => { 
    setModalType(""); 
    setFormData(defaultForm); 
    setSelectedItem(null);
    setFormError("");
  };
  const openConfirm   = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mW = 210, mH = 160, gap = 8;
    let left = rect.right - mW, top = rect.bottom + gap;
    if (left < 12) left = 12;
    if (left + mW > window.innerWidth - 12) left = window.innerWidth - mW - 12;
    if (top + mH > window.innerHeight - 12) top = rect.top - mH - gap;
    setDropdownPos({ top, left });
    setDropdownOpenId(id);
  };

  const depts = useMemo(() => [...new Set(programmes.map(p => p.Department_Faculty).filter(Boolean))], [programmes]);

  const filtered = useMemo(() => programmes.filter(p => {
    const matchSearch = (p.Programme_Name || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (p.Programme_Code || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (p.Department_Faculty || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (p.Specialization || "").toLowerCase().includes(searchText.toLowerCase());
    const matchStatus = filterStatus === "all" || p.Status === filterStatus;
    const matchDept   = filterDept   === "all" || p.Department_Faculty === filterDept;
    return matchSearch && matchStatus && matchDept;
  }), [programmes, searchText, filterStatus, filterDept]);

  const publishedCount = programmes.filter(p => p.Status === "Published").length;
  const draftCount     = programmes.filter(p => p.Status === "Draft").length;
  const totalSeats     = programmes.reduce((s, p) => s + Number(p.Total_Seats || 0), 0);
  const currentDDItem  = programmes.find(p => p._id === dropdownOpenId);

  return (
    <div className="pgp-page">

      {/* ── Header ── */}
      <div className="pgp-header">
        <div className="pgp-header-left">
          <div className="pgp-header-icon">🎓</div>
          <div>
            <div className="pgp-breadcrumb">
              <span>Academics</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              <span className="pgp-breadcrumb-active">PG Programmes</span>
            </div>
            <h1>PG Programmes</h1>
            <p>Manage postgraduate courses, specializations, fees, and eligibility criteria.</p>
          </div>
        </div>
        <button type="button" className="pgp-add-btn" onClick={openAddModal}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Programme
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="pgp-stats">
        {[
          { cls: "total",     icon: "🎓", label: "Total Programmes", val: programmes.length },
          { cls: "published", icon: "🌐", label: "Published",        val: publishedCount },
          { cls: "draft",     icon: "📝", label: "Drafts",           val: draftCount },
          { cls: "seats",     icon: "🪑", label: "Total Seats",      val: totalSeats },
        ].map(s => (
          <div key={s.cls} className={`pgp-stat-card ${s.cls}`}>
            <div className="stat-icon-box">{s.icon}</div>
            <div className="stat-content"><span>{s.label}</span><strong>{s.val}</strong></div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="pgp-filter-card">
        <div className="pgp-filter-grid">
          <div className="fg">
            <label>Search</label>
            <div className="search-input-wrap">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="Name, code, specialization…" value={searchText} onChange={e => setSearchText(e.target.value)} />
              {searchText && <button type="button" className="clear-search" onClick={() => setSearchText("")}>×</button>}
            </div>
          </div>
          <div className="fg">
            <label>Department</label>
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)}>
              <option value="all">All Departments</option>
              {depts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
        </div>
        <div className="pgp-filter-meta">
          Showing <strong>{filtered.length}</strong> of <strong>{programmes.length}</strong> programmes
          {(filterDept !== "all" || filterStatus !== "all" || searchText) && (
            <button type="button" className="clear-filters-btn" onClick={() => { setFilterDept("all"); setFilterStatus("all"); setSearchText(""); }}>Clear filters</button>
          )}
        </div>
      </div>

      {/* ── Cards ── */}
      {loading && <div className="loading-overlay">Loading…</div>}
      {filtered.length > 0 ? (
        <div className="pgp-cards-grid">
          {filtered.map(prog => {
            const color = getDeptColor(prog.Department_Faculty);
            const highlights = prog.Programme_Highlights
              ? prog.Programme_Highlights.split(",").map(h => h.trim()).filter(Boolean)
              : [];
            return (
              <div key={prog._id} className={`pgp-card ${(prog.Status || "").toLowerCase()}`}>
                <div className="pgp-card-bar" style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
                <div className="pgp-card-head">
                  <div className="pgp-card-badge" style={{ background: color + "18", color }}>{prog.Specialization || prog.Department_Faculty || "PG"}</div>
                  <div className="pgp-card-actions">
                    <span className={`pgp-status-dot ${(prog.Status || "").toLowerCase()}`} />
                    <button type="button" className="dots-btn" onClick={e => handleDotsClick(e, prog._id)}>⋮</button>
                  </div>
                </div>
                <div className="pgp-card-body">
                  <div className="pgp-card-code">{prog.Programme_Code}</div>
                  <h3 className="pgp-card-title" onClick={() => openViewModal(prog)}>{prog.Programme_Name}</h3>
                  <p className="pgp-card-desc">{prog.Programme_Description}</p>
                </div>
                <div className="pgp-card-meta">
                  <div className="meta-item"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>{prog.Duration}</div>
                  <div className="meta-item"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>{prog.Total_Seats} Seats</div>
                  <div className="meta-item"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>{prog.Annual_Fees || "—"}/yr</div>
                  <div className="meta-item"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>{prog.Total_Semesters} Semesters</div>
                </div>
                {highlights.length > 0 && (
                  <div className="pgp-card-highlights">
                    {highlights.slice(0, 3).map((h, i) => <span key={i} className="hl-chip" style={{ background: color + "12", color, borderColor: color + "25" }}>{h}</span>)}
                    {highlights.length > 3 && <span className="hl-chip more">+{highlights.length - 3}</span>}
                  </div>
                )}
                <div className="pgp-card-footer">
                  <span className={`pgp-status-badge ${(prog.Status || "").toLowerCase()}`}>{prog.Status === "Published" ? "● Published" : "● Draft"}</span>
                  <div className="pgp-card-btns">
                    <button type="button" className="card-btn-view" onClick={() => openViewModal(prog)}>View</button>
                    <button type="button" className="card-btn-edit" onClick={() => openEditModal(prog)}>Edit</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="pgp-empty">
          <div className="empty-illustration">🎓</div>
          <h3>No programmes found</h3>
          <p>{searchText || filterDept !== "all" || filterStatus !== "all" ? "Try adjusting your filters." : "No PG programmes added yet. Click \"Add Programme\" to get started."}</p>
          {!searchText && filterDept === "all" && filterStatus === "all" && (
            <button type="button" className="pgp-add-btn" onClick={openAddModal}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Programme
            </button>
          )}
        </div>
      )}

      {/* ── Dots Dropdown ── */}
      {dropdownOpenId !== null && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, zIndex: 99999 }}>
          <button type="button" onClick={() => openViewModal(programmes.find(p => p._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
            View Details
          </button>
          <button type="button" onClick={() => openEditModal(programmes.find(p => p._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            Edit Programme
          </button>
          {currentDDItem && (
            <button type="button" className={currentDDItem.Status === "Published" ? "deactivate" : "activate"}
              onClick={() => openConfirm(dropdownOpenId, currentDDItem.Status === "Published" ? "unpublish" : "publish")}>
              {currentDDItem.Status === "Published" ? (
                <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Unpublish</>
              ) : (
                <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>Publish</>
              )}
            </button>
          )}
          <div className="dropdown-divider" />
          <button type="button" className="delete" onClick={() => openConfirm(dropdownOpenId, "delete")}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            Delete
          </button>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => { setConfirmId(null); setConfirmAction(""); }}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className={`confirm-icon-box ${confirmAction}`}>
              {confirmAction === "delete" ? (
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              )}
            </div>
            <h3>{confirmAction === "delete" ? "Delete Programme?" : confirmAction === "unpublish" ? "Unpublish Programme?" : "Publish Programme?"}</h3>
            <p>{confirmAction === "delete" ? "This programme will be permanently deleted." : confirmAction === "unpublish" ? "This programme will be hidden from the public website." : "This programme will be visible on the college website."}</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => { setConfirmId(null); setConfirmAction(""); }}>Cancel</button>
              <button type="button" className={`btn ${confirmAction === "delete" ? "btn-danger" : "btn-warning"}`}
                onClick={() => confirmAction === "delete" ? handleDelete(confirmId) : handleTogglePublish(confirmId)}>
                {confirmAction === "delete" ? "Yes, Delete" : confirmAction === "unpublish" ? "Yes, Unpublish" : "Yes, Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {modalType && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="pgp-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">🎓</div>
                <div>
                  <h2>{modalType === "add" ? "Add PG Programme" : "Edit PG Programme"}</h2>
                  <span>{formData.Programme_Name ? `Editing: ${formData.Programme_Name}` : "Fill in postgraduate programme details below"}</span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form className="modal-form" onSubmit={handleSave}>
              {formError && (
                <div className="modal-error-banner" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", padding: "12px 16px", borderRadius: "10px", fontSize: "13.5px", fontWeight: "500", margin: "16px 24px 0", display: "flex", alignItems: "center", gap: "8px" }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>{formError}</span>
                </div>
              )}
              <div className="tab-content">

                <div className="form-section-label">Programme Identity</div>
                <div className="form-group full">
                  <label>Programme Name <span className="req">*</span></label>
                  <input type="text" value={formData.Programme_Name || ""} onChange={e => setFormData({...formData, Programme_Name: e.target.value})} placeholder="e.g. Master of Science (M.Sc.)" required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Programme Code <span className="req">*</span></label>
                    <input type="text" value={formData.Programme_Code || ""} onChange={e => setFormData({...formData, Programme_Code: e.target.value})} placeholder="e.g. MSC-PHY-01" required />
                  </div>
                  <div className="form-group">
                    <label>Specialization</label>
                    <input type="text" value={formData.Specialization || ""} onChange={e => setFormData({...formData, Specialization: e.target.value})} placeholder="e.g. Organic Chemistry" />
                  </div>
                </div>
                <div className="form-group full">
                  <label>Department / Faculty <span className="req">*</span></label>
                  <input type="text" value={formData.Department_Faculty || ""} onChange={e => setFormData({...formData, Department_Faculty: e.target.value})} placeholder="e.g. Department of Physics" required />
                </div>
                <div className="form-group full">
                  <label>Programme Highlights</label>
                  <input type="text" value={formData.Programme_Highlights || ""} onChange={e => setFormData({...formData, Programme_Highlights: e.target.value})} placeholder="e.g. Research Focus, Seminar, Dissertation (comma-separated)" />
                  <span className="field-hint">Separate with commas — shown as tags on the card.</span>
                </div>

                <div className="form-section-label">Academic Structure</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Duration <span className="req">*</span></label>
                    <input type="text" value={formData.Duration || ""} onChange={e => setFormData({...formData, Duration: e.target.value})} placeholder="e.g. 2 Years" required />
                  </div>
                  <div className="form-group">
                    <label>Total Semesters</label>
                    <input type="number" value={formData.Total_Semesters || ""} onChange={e => setFormData({...formData, Total_Semesters: e.target.value})} placeholder="e.g. 4" min="1" max="8" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Total Seats</label>
                    <input type="number" value={formData.Total_Seats || ""} onChange={e => setFormData({...formData, Total_Seats: e.target.value})} placeholder="e.g. 30" min="1" />
                  </div>
                  <div className="form-group">
                    <label>Annual Fees</label>
                    <input type="text" value={formData.Annual_Fees || ""} onChange={e => setFormData({...formData, Annual_Fees: e.target.value})} placeholder="e.g. ₹18,000" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Mode of Study</label>
                    <select value={formData.Mode_of_Study || "Full Time"} onChange={e => setFormData({...formData, Mode_of_Study: e.target.value})}>
                      {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Affiliation</label>
                    <input type="text" value={formData.Affiliation || ""} onChange={e => setFormData({...formData, Affiliation: e.target.value})} placeholder="e.g. Lucknow University" />
                  </div>
                </div>
                <div className="form-group full">
                  <label>Accreditation / Approval</label>
                  <input type="text" value={formData.Accreditation_Approval || ""} onChange={e => setFormData({...formData, Accreditation_Approval: e.target.value})} placeholder="e.g. NAAC A+, UGC Approved" />
                </div>

                <div className="form-section-label">Admission & Description</div>
                <div className="form-group full">
                  <label>Minimum Qualification</label>
                  <input type="text" value={formData.Minimum_Qualification || ""} onChange={e => setFormData({...formData, Minimum_Qualification: e.target.value})} placeholder="e.g. B.Sc. with minimum 50% marks" />
                </div>
                <div className="form-group full">
                  <label>Eligibility Criteria <span className="req">*</span></label>
                  <textarea value={formData.Eligibility_Criteria || ""} onChange={e => setFormData({...formData, Eligibility_Criteria: e.target.value})} placeholder="e.g. Bachelor's degree in relevant subject with minimum 50% marks…" rows="3" required />
                </div>
                <div className="form-group full">
                  <label>Programme Description <span className="req">*</span></label>
                  <textarea value={formData.Programme_Description || ""} onChange={e => setFormData({...formData, Programme_Description: e.target.value})} placeholder="Describe objectives, research focus, and career scope…" rows="4" required />
                </div>

                <div className="form-section-label">Programme Specializations / Subjects</div>
                <div className="subject-list">
                  {formData.Programme_Subjects && formData.Programme_Subjects.map((sub, idx) => (
                    <div key={sub.id || idx} className="subject-item-card" style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "16px" }}>
                      <div className="sic-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", fontWeight: "600", fontSize: "14px", color: "#475569" }}>
                        <span>Subject #{idx + 1}</span>
                        <button type="button" style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontSize: "13px" }} onClick={() => setFormData({...formData, Programme_Subjects: formData.Programme_Subjects.filter((_, i) => i !== idx)})}>Remove</button>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Subject / Specialization Name</label>
                          <input 
                            type="text"
                            value={sub.Subject_Name || ""} 
                            onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Subject_Name = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                            placeholder="e.g. Advanced Accounting"
                          />
                        </div>
                        <div className="form-group">
                          <label>Total Seats</label>
                          <input 
                            type="number"
                            value={sub.Total_Seats || ""} 
                            onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Total_Seats = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                            placeholder="e.g. 60"
                          />
                        </div>
                        <div className="form-group">
                          <label>Group</label>
                          <input 
                            type="text"
                            value={sub.Group || ""} 
                            onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Group = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                            placeholder="e.g. PG / Elective"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" className="add-sub-btn" style={{ padding: "8px 16px", background: "#f1f5f9", color: "#334155", border: "1px dashed #cbd5e1", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px", width: "100%", marginBottom: "20px" }} onClick={() => setFormData({...formData, Programme_Subjects: [...(formData.Programme_Subjects || []), mkProgrammeSubject()]})}>
                  + Add New Subject
                </button>

                <div className="form-section-label">Publication</div>
                <div className="form-group">
                  <label>Status</label>
                  <div className="status-selector">
                    <label className={`status-option ${formData.Status === "Published" ? "selected" : ""}`}>
                      <input type="radio" name="Status" value="Published" checked={formData.Status === "Published"} onChange={e => setFormData({...formData, Status: e.target.value})} />
                      <div className="status-option-content">
                        <div className="status-icon published">🌐</div>
                        <div><strong>Published</strong><span>Visible on the college website</span></div>
                      </div>
                    </label>
                    <label className={`status-option ${formData.Status === "Draft" ? "selected" : ""}`}>
                      <input type="radio" name="Status" value="Draft" checked={formData.Status === "Draft"} onChange={e => setFormData({...formData, Status: e.target.value})} />
                      <div className="status-option-content">
                        <div className="status-icon draft">📝</div>
                        <div><strong>Draft</strong><span>Hidden from the public</span></div>
                      </div>
                    </label>
                  </div>
                </div>

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? "Saving…" : modalType === "add" ? "Add Programme" : "Save Changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (() => {
        const color  = getDeptColor(selectedItem.Department_Faculty);
        const hlList = selectedItem.Programme_Highlights
          ? selectedItem.Programme_Highlights.split(",").map((h) => h.trim()).filter(Boolean)
          : [];
        return (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="pgp-modal view-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-left">
                  <div className="modal-header-icon" style={{ background: color + "20", color }}>🎓</div>
                  <div>
                    <h2>{selectedItem.Programme_Name}</h2>
                    <span style={{ color }}>{selectedItem.Specialization || selectedItem.Department_Faculty || "PG Programme"}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span className={`pgp-status-badge ${(selectedItem.Status || "").toLowerCase()}`}>
                    {selectedItem.Status === "Published" ? "● Published" : "● Draft"}
                  </span>
                  <button type="button" className="modal-close" onClick={closeModal}>×</button>
                </div>
              </div>

              <div className="view-body">
                {/* Hero */}
                <div className="view-hero" style={{ borderLeft: `5px solid ${color}`, background: `linear-gradient(135deg, ${color}10, ${color}05)` }}>
                  <div className="view-hero-left">
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                      <span className="view-code-tag" style={{ color, background: color + "18" }}>{selectedItem.Programme_Code}</span>
                      {selectedItem.Accreditation_Approval && (
                        <span className="view-accred-tag">{selectedItem.Accreditation_Approval}</span>
                      )}
                    </div>
                    <p>{selectedItem.Programme_Description}</p>
                    {selectedItem.Minimum_Qualification && (
                      <div className="view-qual-tag">
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        Min. Qualification: <strong>{selectedItem.Minimum_Qualification}</strong>
                      </div>
                    )}
                  </div>
                  <div className="view-hero-stats">
                    {[
                      { val: selectedItem.Duration,          label: "Duration"    },
                      { val: selectedItem.Total_Semesters,   label: "Semesters"   },
                      { val: selectedItem.Total_Seats,       label: "Seats"       },
                      { val: selectedItem.Annual_Fees || "—", label: "Annual Fees" },
                    ].map((s) => (
                      <div key={s.label} className="hero-stat">
                        <strong>{s.val}</strong>
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="view-section-label">Programme Information</div>
                <div className="view-info-grid">
                  {[
                    { label: "Mode",           val: selectedItem.Mode_of_Study },
                    { label: "Specialization", val: selectedItem.Specialization || "—" },
                    { label: "Faculty",        val: selectedItem.Department_Faculty || "—" },
                    { label: "Affiliation",    val: selectedItem.Affiliation    || "—" },
                    { label: "Accreditation",  val: selectedItem.Accreditation_Approval  || "—" },
                  ].map((i) => (
                    <div key={i.label} className="view-info-item">
                      <span>{i.label}</span>
                      <strong>{i.val}</strong>
                    </div>
                  ))}
                </div>

                {/* Eligibility */}
                {selectedItem.Eligibility_Criteria && (
                  <>
                    <div className="view-section-label">Eligibility Criteria</div>
                    <div className="view-eligibility-box">
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#059669" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      {selectedItem.Eligibility_Criteria}
                    </div>
                  </>
                )}

                {/* Subjects */}
                {selectedItem.Programme_Subjects && selectedItem.Programme_Subjects.length > 0 && (
                  <>
                    <div className="view-section-label">Specializations / Subjects</div>
                    <div className="view-table-wrapper" style={{ overflowX: "auto", marginBottom: "20px", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>#</th>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>Specialization Name</th>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>Seats</th>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>Group</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedItem.Programme_Subjects.map((sub, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "12px 16px", color: "#64748b" }}>{i + 1}</td>
                              <td style={{ padding: "12px 16px", fontWeight: "500", color: "#1e293b" }}>{sub.Subject_Name || "—"}</td>
                              <td style={{ padding: "12px 16px", color: "#475569" }}>{sub.Total_Seats || "—"}</td>
                              <td style={{ padding: "12px 16px", color: "#475569" }}>{sub.Group || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Highlights */}
                {hlList.length > 0 && (
                  <>
                    <div className="view-section-label">Programme Highlights</div>
                    <div className="view-highlights">
                      {hlList.map((h, i) => (
                        <span key={i} className="highlight-tag" style={{ background: color + "14", color, border: `1px solid ${color}28` }}>
                          ✦ {h}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                <div className="view-footer-actions">
                  <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={() => { closeModal(); openEditModal(selectedItem); }}>
                    Edit Programme
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PGProgrammes;